/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : LrAsAnchorLegalRequirementCardHeader.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CustomAutoCompleteWithAPIV2 from "../../../components/customComponentsV2/common/searchBar/CustomAutoCompleteWithAPIV2";
import { legalRequirements } from "../../../redux/actions";
import { componentStyle } from "../../../config/appTheme";
import { useSelector } from "react-redux";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomSearchbarWithApiV2 from "../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";

function LrAsAnchorLegalRequirementCardHeader({
  setCardOpen,
  getLRListForSelectedChapter,
  lrPage,
  lrRowsPerPage,
  selectedLrObject,
}) {
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  const [selectedLSDetails, setSelectedLSDetails] = useState({});
  const [selectedLSId, setSelectedLSId] = useState(null);
  const [chapterListForSelectedLS, setChapterListForSelectedLS] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);

  useEffect(() => {
    setSelectedLSDetails({});
    setSelectedLSId(null);
    setChapterListForSelectedLS([]);
    setSelectedChapter(null);
  }, [selectedProject?.project_id, selectedVariant?.variant_id]);

  useEffect(() => {
    if (chapterListForSelectedLS?.length > 0) {
      setSelectedChapter((prev) =>
        chapterListForSelectedLS.includes(prev)
          ? prev
          : chapterListForSelectedLS[0],
      );
    } else {
      setSelectedChapter(null);
    }
  }, [chapterListForSelectedLS]);
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography>Select Legal Requirement</Typography>
        <Box
          onClick={() => {
            setCardOpen(false);
          }}
          sx={{ cursor: "pointer" }}
        >
          <KeyboardArrowUpIcon />
        </Box>
      </Box>
      <Box
        display="flex"
        gap={1}
        sx={{
          width: "100%",
          mt: 0.5,
          gap: 1,
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            flex: "0 0 40%",
          }}
        >
          <CustomAutoCompleteWithAPIV2
            key={`${selectedProject?.project_id || "p"}-${
              selectedVariant?.variant_id || "v"
            }`}
            apiAction={
              legalRequirements.get_legal_standards_chapter_for_intersections
            }
            searchKey={"search_ls"}
            placeholder={"Search Legal Standards"}
            firstItemDefaultSelect={true}
            skipInitialSearch={false}
            toolTipLength={30}
            getSelectedItem={(selectedLS) => {
              console.log("---setSelectedLSDetails---", selectedLS);
              setSelectedLSDetails(selectedLS || {});
              setSelectedLSId(selectedLS?.ls_id || null);
              setChapterListForSelectedLS(
                Array.isArray(selectedLS?.chapters) ? selectedLS?.chapters : [],
              );
            }}
            displayKey={"standard_number"}
            valueObj={selectedLSDetails}
            valueKey={"ls_id"}
            enableDarkMode={true}
            txtFldStyle={componentStyle.searchField.textField}
            sx={{
              border: "1px solid #ced4da",
              borderRadius: 1,
              background: "#fff",
              ...componentStyle.searchField.textField,
            }}
            skipApiCall={
              !selectedProject?.project_id || !selectedVariant?.variant_id
            }
            params={{
              project_id: selectedProject?.project_id,
              variant_id: selectedVariant?.variant_id,
            }}
          />
        </Box>

        <Box
          sx={{
            flex: "0 0 58%",
            "& .MuiInputBase-input": {
              fontSize: 14,
            },
            "& .MuiInputBase-input::placeholder": {
              fontSize: 14,
            },
            "& .MuiInputLabel-root": {
              fontSize: 14,
            },
            "& .MuiSelect-select": {
              fontSize: 14,
            },
            "& .MuiSelect-select .MuiTypography-root": {
              fontSize: 14,
            },
          }}
        >
          <CustomSelectV2
            placeholder={"Select Chapter"}
            label={"Select Chapter"}
            showLabel={false}
            optionsList={chapterListForSelectedLS?.map((c) => ({
              label: c,
              value: c,
            }))}
            selectedValue={selectedChapter}
            textLength={15}
            handleOnChange={(e) => {
              setSelectedChapter(e.target.value);
            }}
            dark={true}
          />
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          mt: 0.5,
        }}
      >
        <CustomSearchbarWithApiV2
          key={`left-lr-${selectedLSId}-${selectedChapter}`}
          apiAction={legalRequirements?.get_legal_requirement_for_intersections}
          searchKey={"search_lr"}
          placeholder={"Search Legal Requirements"}
          getDataOnSearch={(data, resp) => {
            console.log("---requirements---", data, resp);
            getLRListForSelectedChapter(data, resp.total_count);
          }}
          onSearchQueryChange={(search, prev) => {
            console.log("---Legal Requirements---", search, prev);
          }}
          skipApiCall={
            !selectedLSId ||
            !selectedChapter ||
            !selectedProject?.project_id ||
            !selectedVariant?.variant_id
          }
          params={{
            project_id: selectedProject?.project_id,
            variant_id: selectedVariant?.variant_id,
            ls_id: selectedLSId,
            chapter: selectedChapter,
            page_number: lrPage,
            page_size: lrRowsPerPage,
          }}
          iconColor={"white"}
          txtFldStyle={componentStyle.searchField.textField}
        />
      </Box>
    </Box>
  );
}

LrAsAnchorLegalRequirementCardHeader.propTypes = {
  setCardOpen: PropTypes.func,
  getLRListForSelectedChapter: PropTypes.func,
  lrPage: PropTypes.number,
  lrRowsPerPage: PropTypes.number,
  selectedLrObject: PropTypes.object,
};

export default LrAsAnchorLegalRequirementCardHeader;
